import React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Inbox, Loader2, Search, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";

/* ---------------------------------- Modal ---------------------------------- */

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        className={`flex max-h-[90vh] w-full flex-col rounded-2xl bg-white shadow-modal ${wide ? "max-w-3xl" : "max-w-xl"}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h3 className="text-lg font-bold text-ink">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-2 text-muted transition-colors hover:bg-slate-100 hover:text-ink" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-3 border-t border-line px-6 py-4">{footer}</div>}
      </div>
    </div>
  );
}

/* ---------------------------------- Badge --------------------------------- */

export type BadgeTone =
  | "live"
  | "completed"
  | "upcoming"
  | "archived"
  | "failed"
  | "pending"
  | "active"
  | "inactive"
  | "inprogress"
  | "cancelled";

const badgeTones: Record<BadgeTone, string> = {
  live: "bg-success text-white",
  completed: "bg-slate-100 text-slate-600",
  upcoming: "bg-primary-soft text-primary",
  archived: "bg-slate-100 text-slate-500",
  failed: "bg-danger-soft text-danger",
  pending: "bg-warning-soft text-warning",
  active: "bg-success-soft text-success",
  inactive: "bg-slate-100 text-slate-500",
  inprogress: "bg-warning-soft text-warning",
  cancelled: "bg-slate-100 text-slate-500",
};

export function Badge({ tone, children, dot }: { tone: BadgeTone; children: React.ReactNode; dot?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${badgeTones[tone]}`}
    >
      {(tone === "live" || dot) && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {children}
    </span>
  );
}

export function statusTone(status: string): BadgeTone {
  const s = status.toUpperCase();
  if (s === "LIVE") return "live";
  if (s === "COMPLETED") return "completed";
  if (s === "UPCOMING") return "upcoming";
  if (s === "ARCHIVED") return "archived";
  if (s === "FAILED") return "failed";
  if (s === "IN PROGRESS") return "inprogress";
  if (s === "ACTIVE") return "active";
  if (s === "INACTIVE") return "inactive";
  if (s === "CANCELLED") return "cancelled";
  return "pending";
}

/* --------------------------------- Button --------------------------------- */

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "dark";

const buttonVariants: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white hover:bg-primary-hover shadow-sm",
  secondary: "bg-white text-ink border border-line hover:bg-slate-50",
  danger: "bg-danger text-white hover:bg-red-700",
  ghost: "bg-transparent text-muted hover:bg-slate-100 hover:text-ink",
  dark: "bg-navy-950 text-white hover:bg-navy-800",
};

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
}) {
  const sizes = { sm: "h-7 px-2.5 text-xs", md: "h-9 px-3.5 text-[13px]", lg: "h-11 px-5 text-sm" };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all duration-150 active:scale-[0.98] disabled:opacity-50 ${buttonVariants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* -------------------------------- StatCard -------------------------------- */

export function StatCard({
  icon,
  value,
  label,
  sub,
  subTone = "muted",
  iconBg = "bg-primary-soft text-primary",
}: {
  icon: React.ReactNode;
  value: React.ReactNode;
  label: string;
  sub?: string;
  subTone?: "muted" | "success" | "primary" | "warning";
  iconBg?: string;
}) {
  const tones = { muted: "text-muted", success: "text-success", primary: "text-primary", warning: "text-warning" };
  return (
    <div className="card flex items-center gap-2.5 p-2.5 transition-shadow hover:shadow-lift">
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconBg}`}>{icon}</span>
      <div className="min-w-0">
        <div className="text-base font-bold leading-tight text-ink">{value}</div>
        <div className="truncate text-[11px] font-medium leading-tight text-muted">{label}</div>
        {sub && <div className={`mt-0.5 text-[11px] font-semibold ${tones[subTone]}`}>{sub}</div>}
      </div>
    </div>
  );
}

/* ------------------------------- SearchInput ------------------------------- */

export function SearchInput({ placeholder, className = "" }: { placeholder: string; className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input className="input pl-9" placeholder={placeholder} />
    </div>
  );
}

/* ---------------------------------- Select --------------------------------- */

/*
 * An option is either a plain label, or a value with a label of its own.
 *
 * The second form matters wherever the thing being chosen has an identity
 * separate from its name: two competitions can be named the same and still be
 * different competitions, so a filter keyed on the name picks the wrong one.
 */
export type SelectOption = string | { value: string; label: string };

export function Select({
  options,
  value,
  onChange,
  className = "",
}: {
  options: SelectOption[];
  value?: string;
  onChange?: (v: string) => void;
  className?: string;
}) {
  const items = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <div className={`relative ${className}`}>
      <select
        className="input appearance-none pr-8 font-medium"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      >
        {/* Indexed, so a caller passing repeated values still renders. */}
        {items.map((o, i) => (
          <option key={`${o.value}-${i}`} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

/* ----------------------------------- Tabs ---------------------------------- */

export function Tabs({
  tabs,
  active,
  onChange,
  className = "",
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
  className?: string;
}) {
  return (
    // Tab strips scroll sideways on phones rather than wrapping into a mess
    <div className={`slim-scrollbar flex gap-1 overflow-x-auto overflow-y-hidden border-b border-line ${className}`}>
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t)}
          className={`-mb-px shrink-0 whitespace-nowrap border-b-2 px-3 py-1.5 text-[13px] font-semibold transition-colors ${
            active === t ? "border-primary text-primary" : "border-transparent text-muted hover:text-ink"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------- Pagination ------------------------------- */

/*
 * The count line under a table.
 *
 * Results are read as a whole — a competitor looks for their own name and a
 * scorer checks a field of fifty against the sheet in front of them. Breaking
 * that into five pages of ten made both jobs worse, and the page controls were
 * never wired up anyway: the numbers moved, the rows-per-page dropdown did
 * nothing, and First and Last did nothing at all.
 *
 * So tables now show every row and scroll inside their own card, and this is
 * just the count. `page`/`pages`/`onPage` are still accepted so the paging
 * arrows can come back for a genuinely huge table without another rewrite —
 * they simply render nothing while there is one page.
 */
export function Pagination({
  page = 1,
  pages = 1,
  summary,
  onPage,
}: {
  page?: number;
  pages?: number;
  summary?: string;
  onPage?: (p: number) => void;
}) {
  const btn =
    "flex h-7 min-w-[30px] items-center justify-center rounded-md border border-line bg-white px-1.5 text-[13px] font-medium text-muted transition-colors hover:bg-slate-50 hover:text-ink disabled:opacity-40";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2">
      <span className="text-[13px] text-muted">{summary}</span>
      {pages > 1 && (
        <div className="flex items-center gap-1.5">
          <button className={btn} aria-label="First page" disabled={page === 1} onClick={() => onPage?.(1)}>
            <ChevronsLeft size={15} />
          </button>
          <button className={btn} aria-label="Previous page" disabled={page === 1} onClick={() => onPage?.(Math.max(1, page - 1))}>
            <ChevronLeft size={15} />
          </button>
          <span className="px-1.5 text-[13px] font-semibold text-ink">
            {page} / {pages}
          </span>
          <button className={btn} aria-label="Next page" disabled={page === pages} onClick={() => onPage?.(Math.min(pages, page + 1))}>
            <ChevronRight size={15} />
          </button>
          <button className={btn} aria-label="Last page" disabled={page === pages} onClick={() => onPage?.(pages)}>
            <ChevronsRight size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Avatar ---------------------------------- */

const avatarPalette = ["#2563EB", "#7C3AED", "#0891B2", "#16A34A", "#EA580C", "#DB2777", "#4F46E5", "#0D9488"];

export function Avatar({ name, size = 34 }: { name: string; size?: number }) {
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");
  const color = avatarPalette[(name.charCodeAt(0) + name.length) % avatarPalette.length];
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-full font-bold text-white"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.36 }}
    >
      {initials}
    </span>
  );
}

/* -------------------------------- RankChip --------------------------------- */

export function RankChip({ rank }: { rank: number }) {
  if (rank === 1)
    return <span className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-400 text-xs font-extrabold text-white shadow-sm">1</span>;
  if (rank === 2)
    return <span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-300 text-xs font-extrabold text-white shadow-sm">2</span>;
  if (rank === 3)
    return <span className="flex h-7 w-7 items-center justify-center rounded-md bg-orange-400 text-xs font-extrabold text-white shadow-sm">3</span>;
  return <span className="flex h-7 w-7 items-center justify-center text-sm font-semibold text-ink">{rank}</span>;
}

/* ------------------------------- CompThumb --------------------------------- */

const thumbGradients: Record<string, string> = {
  sunset: "from-orange-400 via-red-500 to-navy-900",
  target: "from-slate-100 to-slate-300",
  range: "from-sky-300 via-sky-500 to-emerald-700",
  rifle: "from-slate-500 via-navy-800 to-navy-950",
};

export function CompThumb({ kind, size = 56, rounded = "rounded-lg" }: { kind: string; size?: number; rounded?: string }) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center bg-gradient-to-br ${thumbGradients[kind] ?? thumbGradients.target} ${rounded}`}
      style={{ width: size, height: size }}
    >
      {kind === "target" && (
        <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="17" fill="#fff" stroke="#0F172A" strokeWidth="2" />
          <circle cx="20" cy="20" r="10" fill="none" stroke="#0F172A" strokeWidth="2" />
          <circle cx="20" cy="20" r="4.5" fill="#DC2626" />
        </svg>
      )}
    </span>
  );
}

/* ------------------------------- InfoRows ---------------------------------- */

export function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-[13px]">
      <span className="text-muted">{label}</span>
      <span className="text-right font-semibold text-ink">{value}</span>
    </div>
  );
}

/* ------------------------------ SectionCard -------------------------------- */

export function SectionCard({
  title,
  action,
  children,
  className = "",
  bodyClassName = "",
}: {
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <div className={`card ${className}`}>
      <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <h3 className="text-sm font-bold text-ink">{title}</h3>
        {action}
      </div>
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

/* --------------------------------- Toggle ---------------------------------- */

export function Toggle({ on, onChange }: { on: boolean; onChange?: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange?.(!on)}
      className={`relative h-6 w-11 rounded-full transition-colors ${on ? "bg-primary" : "bg-slate-300"}`}
      role="switch"
      aria-checked={on}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`}
      />
    </button>
  );
}

/* -------------------------------- LineChart -------------------------------- */

export function LineChart({
  data,
  height = 200,
  formatValue = (v: number) => v.toLocaleString(),
}: {
  data: { label: string; value: number }[];
  height?: number;
  formatValue?: (v: number) => string;
}) {
  const w = 640;
  const h = height;
  const pad = { l: 44, r: 16, t: 24, b: 28 };
  // A single point has no line to draw and an empty series would give ±Infinity.
  if (data.length < 2) {
    return <div className="flex items-center justify-center p-8 text-[13px] text-muted" style={{ height }}>Not enough data to chart yet.</div>;
  }
  const min = Math.min(...data.map((d) => d.value));
  const max = Math.max(...data.map((d) => d.value));
  const span = max - min || 1;
  const px = (i: number) => pad.l + (i / (data.length - 1)) * (w - pad.l - pad.r);
  const py = (v: number) => pad.t + (1 - (v - min) / span) * (h - pad.t - pad.b);
  const line = data.map((d, i) => `${i === 0 ? "M" : "L"}${px(i)},${py(d.value)}`).join(" ");
  const area = `${line} L${px(data.length - 1)},${h - pad.b} L${px(0)},${h - pad.b} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
      <defs>
        <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563EB" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => (
        <line key={t} x1={pad.l} x2={w - pad.r} y1={pad.t + t * (h - pad.t - pad.b)} y2={pad.t + t * (h - pad.t - pad.b)} stroke="#E6EAF2" strokeWidth="1" />
      ))}
      <path d={area} fill="url(#chartFill)" />
      <path d={line} fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={px(i)} cy={py(d.value)} r="4" fill="#fff" stroke="#2563EB" strokeWidth="2.5" />
          <text x={px(i)} y={py(d.value) - 10} textAnchor="middle" className="fill-ink" fontSize="10" fontWeight="600">
            {formatValue(d.value)}
          </text>
          <text x={px(i)} y={h - 8} textAnchor="middle" fontSize="10" className="fill-slate-400">
            {d.label}
          </text>
        </g>
      ))}
    </svg>
  );
}

/* --------------------------- Loading / empty states -------------------------- */
/*
 * The portal ships with no data — everything arrives by importing a shoot
 * workbook. These render the "nothing here yet" case consistently instead of
 * leaving a page blank or half-drawn.
 */

export function Loading({ label = "Loading...", className = "" }: { label?: string; className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-2.5 p-10 text-sm text-muted ${className}`}>
      <Loader2 size={16} className="animate-spin text-primary" />
      {label}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  icon: Icon = Inbox,
  action,
  className = "",
}: {
  title: string;
  message?: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center justify-center px-6 py-14 text-center ${className}`}>
      <Icon size={40} className="text-slate-300" strokeWidth={1.25} />
      <h3 className="mt-4 text-[15px] font-bold text-ink">{title}</h3>
      {message && <p className="mt-1.5 max-w-md text-[13px] leading-relaxed text-muted">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Empty state pointing an administrator at the import page. */
export function NoDataYet({ what, className = "" }: { what: string; className?: string }) {
  return (
    <EmptyState
      className={className}
      title={`No ${what} yet`}
      message={
        <>
          {what[0].toUpperCase() + what.slice(1)} appear once a shoot is imported. Sign in to the admin portal and upload a
          shoot workbook on <b className="text-ink">Import Data</b>.
        </>
      }
      action={
        <Link
          to="/admin/import"
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-[13px] font-bold text-white transition-colors hover:bg-primary-hover"
        >
          <Upload size={14} /> Go to Import Data
        </Link>
      }
    />
  );
}

/*
 * The Event Type, as the Event sheet writes it — OPM, SERIES, TRIALS, CLUB,
 * and whatever else a club adds. Known types get a colour; anything else
 * still gets a readable chip rather than an unstyled one, because the
 * register is a list a club maintains and not a list we control.
 */
const EVENT_TYPE_TONE: Record<string, string> = {
  OPM: "bg-warning-soft text-warning",
  OPEN: "bg-warning-soft text-warning",
  SERIES: "bg-violet-50 text-violet-600",
  "GRAND PRIX": "bg-primary-soft text-primary",
  TRIALS: "bg-success-soft text-success",
  CLUB: "bg-amber-50 text-amber-600",
  "LOCAL COMPETITION": "bg-amber-50 text-amber-600",
};

export function EventTypeChip({ type }: { type?: string | null }) {
  const key = String(type || "").toUpperCase();
  if (!key) return <span className="text-muted">—</span>;
  return (
    <span
      className={`rounded-md px-2.5 py-1 text-xs font-bold ${EVENT_TYPE_TONE[key] || "bg-slate-100 text-ink"}`}
    >
      {type}
    </span>
  );
}
